import { ItemReorderEventDetail } from '@ionic/core';
import { IonCol, IonGrid, IonReorder, IonReorderGroup, IonRouterLink, IonRow } from '@ionic/react';
import equal from 'deep-equal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ArrayParam,
  BooleanParam,
  createEnumParam,
  QueryParamConfig,
  useQueryParams,
  withDefault
} from 'use-query-params';

import PrimaryButton from '@components/buttons/PrimaryButton';
import TertiaryButton from '@components/buttons/TertiaryButton';
import BasicCard from '@components/cards/BasicCard';
import ArchiveSegment from '@components/carePlan/ArchiveSegment';
import styles from '@components/carePlan/ExpertCarePlan.module.css';
import GoalEditor from '@components/carePlan/GoalEditor';
import GoalPopover from '@components/carePlan/GoalPopover';
import GoalsManager, { Section } from '@components/carePlan/GoalsManager';
import NextConsult from '@components/profile/expert/NextConsult';
import Icon from '@components/text/Icon';
import Heading2 from '@components/typography/Heading2';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import {
  GetJourneyWithPlanQuery,
  GoalFragment,
  GoalPublishDocument,
  GoalPublishMutation,
  GoalPublishMutationVariables,
  GoalsReorderDocument,
  GoalsReorderMutation,
  GoalsReorderMutationVariables
} from '@typing/Generated';
import { moveArrayByIndex, sortByPosition } from '@utils/modelUtils';

type Journey = GetJourneyWithPlanQuery['journey'];
type GoalType = GoalFragment;

type Props = {
  journey: Journey;
};

type GoalCardProps = {
  allGoals: boolean;
  goal: GoalType;
  handleGoalSelect: (goal: GoalType) => void;
  journey: Journey;
  selectedGoals: GoalType[];
};

const GoalCard = ({ allGoals, goal, handleGoalSelect, journey, selectedGoals }: GoalCardProps) => {
  const [publishLoading, setPublishLoading] = useState(false);

  const [publishGoal] = useMutationWithValidation<GoalPublishMutation, GoalPublishMutationVariables>(
    GoalPublishDocument,
    'goal',
    {
      update: store => {
        if (goal?.draft) {
          store.modify({
            fields: {
              draft() {
                return false;
              }
            },
            id: store.identify({ ...goal })
          });
        }

        goal?.carePlanActionItems
          .filter(aa => aa.draft)
          .map(aa => {
            store.modify({
              fields: {
                draft() {
                  return false;
                }
              },
              id: store.identify({ ...aa })
            });
          });

        goal?.tasks
          .filter(task => task.draft)
          .map(aa => {
            store.modify({
              fields: {
                draft() {
                  return false;
                }
              },
              id: store.identify({ ...aa })
            });
          });

        const resources = goal?.tasks.flatMap(task => task.resources);

        resources
          ?.filter(resource => resource.draft)
          .map(resource => {
            store.modify({
              fields: {
                draft() {
                  return false;
                }
              },
              id: store.identify({ ...resource })
            });
          });
      }
    }
  );

  const draftDisabled = useMemo(() => {
    if (publishLoading) {
      return true;
    }
    if (
      (goal &&
        !goal.draft &&
        !goal.carePlanActionItems.some(ai => ai.draft) &&
        !goal.tasks.some(task => task.draft) &&
        !goal.tasks.some(task => task.resources.some(resource => resource.draft))) ||
      (goal &&
        goal.tasks.length === 0 &&
        goal?.carePlanActionItems.length === 0 &&
        goal.articles.length === 0 &&
        goal.tools.length === 0 &&
        goal.worksheets.length === 0)
    ) {
      return true;
    }
  }, [goal, publishLoading]);

  return (
    <IonRouterLink
      key={goal.id}
      onClick={() => {
        handleGoalSelect(goal);
      }}
    >
      <BasicCard
        active={!allGoals && selectedGoals.length === 1 && selectedGoals[0].id === goal.id}
        background={goal.draft ? 'neutral100' : undefined}
        hover="purple"
        inactive={goal.archived || goal.draft}
      >
        <IonGrid className={styles.goal}>
          <IonRow>
            <IonCol size="auto">
              <div className={styles.reorderContainer}>
                <IonReorder
                  className={
                    !allGoals && selectedGoals.length === 1 && selectedGoals[0].id === goal.id
                      ? styles.reorderIconSelected
                      : styles.reorderIcon
                  }
                  slot="end"
                >
                  <div className={styles.reorderIcons}>
                    <Icon
                      ariaLabelKey="pages.carePlan.base.goals.reorder"
                      className={styles.chevronIcon}
                      icon="caretUp"
                    />
                    <Icon
                      ariaLabelKey="pages.carePlan.base.goals.reorder"
                      className={styles.chevronIcon}
                      icon="caretDown"
                    />
                  </div>
                </IonReorder>
              </div>
            </IonCol>
            <IonCol>
              <Heading2 bold={false} color={goal.draft ? 'neutral300' : undefined}>
                {goal.title}
              </Heading2>
            </IonCol>
            <IonCol size="auto">
              <div className={styles.actions}>
                <GoalPopover
                  color={
                    !allGoals && selectedGoals.length === 1 && selectedGoals[0].id === goal.id
                      ? 'shadesWhite'
                      : undefined
                  }
                  goal={goal}
                  journey={journey}
                />
              </div>
            </IonCol>
          </IonRow>
          {!goal.draft && (
            <IonRow>
              <IonCol className="align-right">
                <PrimaryButton
                  disabled={draftDisabled}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPublishLoading(true);
                    publishGoal({
                      id: goal.id
                    }).then(() => {
                      setPublishLoading(false);
                    });
                  }}
                  size="small"
                  skipHover
                  textKey={
                    !goal.draft &&
                    !goal.carePlanActionItems.some(ai => ai.draft) &&
                    !goal.tasks.some(task => task.draft) &&
                    !goal.tasks.some(task => task.resources.some(resource => resource.draft))
                      ? 'pages.carePlan.base.goals.published'
                      : 'pages.carePlan.base.goals.publish'
                  }
                />
              </IonCol>
            </IonRow>
          )}
        </IonGrid>
      </BasicCard>
    </IonRouterLink>
  );
};

const ExpertCarePlan = ({ journey }: Props) => {
  const sectionParam = createEnumParam<Section>(Object.values(Section));

  const [query, setQuery] = useQueryParams({
    allGoals: withDefault({ ...BooleanParam, urlName: 'expertCarePlanAllGoals' }, true),
    currentSection: withDefault(
      { ...sectionParam, urlName: 'expertCarePlanCurrentSection' },
      journey.goals.filter(g => !g.archived).length > 1 ? Section.CARE_PARTNER_TASKS : Section.ALL
    ),
    selectedGoalIds: withDefault(
      { ...ArrayParam, urlName: 'expertCarePlanGoalIds' },
      sortByPosition(journey.goals).map(g => g.id)
    ) as QueryParamConfig<string[]>
  });

  const [allGoals, setAllGoals] = useState(query.allGoals);
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>(query.selectedGoalIds);
  const [currentSection, setCurrentSection] = useState<Section>(query.currentSection);

  useEffect(() => {
    const values = { allGoals, currentSection, selectedGoalIds };
    if (!equal(values, query)) {
      setQuery(values);
    }
  }, [allGoals, currentSection, query, selectedGoalIds, setQuery]);

  const [pollingGoalId, setPollingGoalId] = useState<string | null>(null);
  const [archived, setArchived] = useState(false);
  const goalEditorRef = useRef<HTMLDivElement>(null);

  const goals = sortByPosition(journey.goals.filter(goal => goal.archived === archived));

  const selectedGoals = sortByPosition(
    goals.filter(goal => selectedGoalIds.some(gid => gid === goal.id) && !goal.draft)
  ).concat(goals.filter(g => g.draft && selectedGoalIds.some(gid => gid === g.id)).reverse());

  const [goalsReorder] = useMutationWithValidation<GoalsReorderMutation, GoalsReorderMutationVariables>(
    GoalsReorderDocument,
    'goal'
  );

  const reorderGoals = useCallback(
    (event: CustomEvent<ItemReorderEventDetail>) => {
      const newGoalsOrder = moveArrayByIndex(
        event,
        goals.filter(goal => !goal.draft)
      ).map(goal => goal.id);

      if (archived) {
        sortByPosition(journey.goals.filter(goal => goal.archived !== archived && !goal.draft))
          .reverse()
          .forEach(g => newGoalsOrder.unshift(g.id));
      } else {
        sortByPosition(journey.goals.filter(goal => goal.archived !== archived && !goal.draft))
          .reverse()
          .forEach(g => newGoalsOrder.push(g.id));
      }

      goalsReorder({
        goalIds: journey.goals
          .filter(goal => goal.draft)
          .map(goal => goal.id)
          .concat(newGoalsOrder),
        journeyId: journey.id
      }).then(() => {
        event.detail.complete();
      });
    },
    [goals, archived, goalsReorder, journey.id, journey.goals]
  );

  const handleGoalCreate = () => {
    setAllGoals(false);
    setSelectedGoalIds([]);
    const boundingClient = goalEditorRef.current?.getBoundingClientRect();
    if (boundingClient && boundingClient.top < 0) {
      setTimeout(() => goalEditorRef.current?.scrollIntoView({ behavior: 'smooth' }), 500);
    }
  };

  const handleAllGoals = () => {
    setAllGoals(true);
    setSelectedGoalIds(journey.goals.map(goal => goal.id));
  };

  const handleGoalSelect = (goal: GoalFragment) => {
    setAllGoals(false);
    setSelectedGoalIds([goal.id]);
    setCurrentSection(Section.ALL);
    const boundingClient = goalEditorRef.current?.getBoundingClientRect();
    if (boundingClient && boundingClient.top < 0) {
      setTimeout(() => goalEditorRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  useEffect(() => {
    if (pollingGoalId && !selectedGoals.some(g => g.id === pollingGoalId)) {
      const interval = setInterval(() => {
        const goal = journey.goals.find(goal => goal.id === pollingGoalId);

        if (goal) {
          setPollingGoalId(null);
          setSelectedGoalIds([goal.id]);
          setCurrentSection(Section.ALL);
        }
      }, 200);

      return () => clearInterval(interval);
    }
  }, [journey.goals, pollingGoalId, selectedGoals]);

  return (
    <div>
      <NextConsult journey={journey} />
      <div className={styles.expertGrid}>
        <div className={styles.cardContainer}>
          <ArchiveSegment
            count={journey.goals.filter(goal => goal.archived !== archived && !goal.draft).length}
            setValue={setArchived}
            value={archived}
          />
          <div className={styles.goals}>
            <IonRouterLink onClick={handleAllGoals}>
              <BasicCard active={allGoals || selectedGoals.length > 1} boxShadow hover={!allGoals ? 'purple' : 'none'}>
                <div className={styles.goalCard}>
                  <Heading2 bold={false}>
                    <FormattedMessage id="pages.carePlan.base.goals.all" />
                  </Heading2>
                </div>
              </BasicCard>
            </IonRouterLink>

            <IonReorderGroup className={styles.goals} disabled={false} onIonItemReorder={reorderGoals}>
              {goals
                .filter(g => !g.draft)
                .map(goal => (
                  <GoalCard
                    allGoals={allGoals}
                    goal={goal}
                    handleGoalSelect={handleGoalSelect}
                    journey={journey}
                    key={goal.id}
                    selectedGoals={selectedGoals}
                  />
                ))}
            </IonReorderGroup>
            {goals.filter(g => g.draft).length > 0 && (
              <>
                <div className={styles.spacing}>
                  <Heading2 color="primary700">
                    <FormattedMessage id="pages.carePlan.base.goals.draft" />
                  </Heading2>
                </div>
                {goals
                  .filter(g => g.draft)
                  .reverse()
                  .map(goal => (
                    <GoalCard
                      allGoals={allGoals}
                      goal={goal}
                      handleGoalSelect={handleGoalSelect}
                      journey={journey}
                      key={goal.id}
                      selectedGoals={selectedGoals}
                    />
                  ))}
              </>
            )}

            <div className={styles.addGoal}>
              <TertiaryButton
                icon="add"
                iconPosition="end"
                large
                onClick={handleGoalCreate}
                textKey="pages.carePlan.base.goals.addAGoal"
              />
            </div>
          </div>
        </div>
        <div className={styles.fullWidth} ref={goalEditorRef}>
          {!allGoals && (selectedGoals.length === 0 || (selectedGoals.length === 1 && selectedGoals[0].draft)) ? (
            <div className={styles.fullWidth}>
              <GoalEditor
                goal={selectedGoals.length ? selectedGoals[0] : null}
                journey={journey}
                setSelectedGoalId={setPollingGoalId}
              />
            </div>
          ) : (
            <div className={styles.fullWidth}>
              <GoalsManager
                currentSection={currentSection}
                goals={selectedGoals}
                journey={journey}
                setCurrentSection={setCurrentSection}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpertCarePlan;
